/**
 * @file SelectDropdownContainer — behavior wiring for the shared selection
 * dropdown.
 * @description Owns the `useDropdownPositioning` wiring for the
 * `FixedPositionDropdownShell` surface (SE-5570) and decorates each option's
 * `onPress` so a row press fires the caller handler and then requests close —
 * unless `keepOpenOnSelect` is set (multi-interact dropdowns such as the
 * squad Targets list filter). Decorated options are memoized so the view
 * receives stable handler references. No data fetching — cross-feature
 * presentational primitive.
 * @module components/molecules/SelectDropdown/SelectDropdownContainer
 */
import React, { useMemo } from "react";

import { useDropdownPositioning } from "@/components/atoms/FixedPositionDropdownShell";

import SelectDropdownView, {
  splitPinnedGroup,
  type SelectDropdownOption,
  type SelectDropdownProps,
} from "./SelectDropdownView";

/** Default minimum panel width in px — the SE-5149 option-row floor. */
export const SELECT_DROPDOWN_MIN_WIDTH = 192;

/** Default panel max height in px (internal scroll + clamp/flip). */
export const SELECT_DROPDOWN_MAX_HEIGHT = 280;

/**
 * SelectDropdown — shared selection-dropdown primitive on the shell.
 * @param props - Public props.
 * @param props.isOpen - Whether the dropdown is open.
 * @param props.onClose - Invoked when the dropdown requests close.
 * @param props.onToggle - Toggles the dropdown (trigger press).
 * @param props.triggerContent - Anchor visual content.
 * @param props.triggerTestID - testID for the trigger pressable.
 * @param props.triggerAccessibilityLabel - A11y label for the trigger.
 * @param props.triggerClassName - Classes for the trigger pressable.
 * @param props.rootClassName - Classes for the shell's relative wrapper — the
 * element that acts as the flex item at the call site.
 * @param props.options - Option rows to render.
 * @param props.anchor - Which trigger edge the panel hugs (default "left").
 * @param props.panelWidth - Fixed panel width in px.
 * @param props.panelMinWidth - Minimum panel width (default 192).
 * @param props.panelHeight - Panel max height (default 280).
 * @param props.keepOpenOnSelect - Keep the panel open after selecting.
 * @param props.panelClassName - Extra classes for the panel.
 * @param props.rowClassName - Extra classes for every row.
 * @param props.header - Optional heading above the first row.
 * @param props.searchValue - Current text in the panel's search field.
 * @param props.onSearchChange - Keystroke handler; providing it mounts the field.
 * @param props.searchPlaceholder - Placeholder + accessible name for the field.
 * @param props.emptyText - Message shown in place of the rows when there are none.
 * @param props.selectionIndicator - How rows announce selection (default "check").
 * @param props.testID - Root testID.
 * @returns The anchored selection dropdown.
 */
const SelectDropdownContainer = ({
  isOpen,
  onClose,
  onToggle,
  triggerContent,
  triggerTestID,
  triggerAccessibilityLabel,
  triggerClassName,
  rootClassName,
  options,
  anchor = "left",
  panelWidth,
  panelMinWidth = SELECT_DROPDOWN_MIN_WIDTH,
  panelHeight = SELECT_DROPDOWN_MAX_HEIGHT,
  keepOpenOnSelect = false,
  panelClassName,
  rowClassName,
  header,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  emptyText,
  selectionIndicator,
  testID,
}: SelectDropdownProps): React.ReactElement => {
  const { triggerRef, panelGeometry, shouldShow } = useDropdownPositioning({
    isOpen,
    anchor,
    panelWidth,
    panelMinWidth,
    panelHeight,
  });

  const decoratedOptions = useMemo<SelectDropdownOption[]>(
    () =>
      options.map(option => ({
        ...option,
        onPress: (): void => {
          option.onPress();
          if (!keepOpenOnSelect) onClose();
        },
      })),
    [options, keepOpenOnSelect, onClose]
  );

  const splitOptions = useMemo(
    () => splitPinnedGroup(decoratedOptions),
    [decoratedOptions]
  );

  return (
    <SelectDropdownView
      isOpen={isOpen}
      onClose={onClose}
      onToggle={onToggle}
      triggerContent={triggerContent}
      triggerTestID={triggerTestID}
      triggerAccessibilityLabel={triggerAccessibilityLabel}
      triggerClassName={triggerClassName}
      rootClassName={rootClassName}
      scrollingOptions={splitOptions.scrolling}
      pinnedOptions={splitOptions.pinned}
      panelClassName={panelClassName}
      rowClassName={rowClassName}
      header={header}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      searchPlaceholder={searchPlaceholder}
      emptyText={emptyText}
      selectionIndicator={selectionIndicator}
      testID={testID}
      triggerRef={triggerRef}
      panelGeometry={panelGeometry}
      shouldShow={shouldShow}
    />
  );
};

SelectDropdownContainer.displayName = "SelectDropdownContainer";

export default SelectDropdownContainer;
